import { useEffect, useRef } from 'react';
import { MODEL_VERSION, MODEL_VERSION_SETTINGS_MAP } from '@/app/constants';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormImagePrompt, MultiFrameTransition } from '@/app/types';
import { getImg2VideoMaxImageCount, validateAndFixDuration } from '@/app/utils';

interface UseImg2VideoModelVersionSwitchOptions {
  imagePrompts: (FormImagePrompt | null)[];
  setImages: (images: FormImagePrompt[]) => void;
  setCurrentTransitionIndex?: (index: number) => void;
}

export type Img2VideoModeSwitchSnapshot = {
  images: FormImagePrompt[];
  multiFrameTransitions?: MultiFrameTransition[];
};

export const mergeImg2VideoModeSwitchImages = (
  visibleImages: FormImagePrompt[],
  savedImages: FormImagePrompt[],
): FormImagePrompt[] => {
  const seenIds = new Set<string>();

  return [...visibleImages, ...savedImages].filter((image) => {
    if (seenIds.has(image.id)) return false;
    seenIds.add(image.id);
    return true;
  });
};

export const useImg2VideoModelVersionSwitch = ({
  imagePrompts,
  setImages,
  setCurrentTransitionIndex,
}: UseImg2VideoModelVersionSwitchOptions) => {
  const { modelVersion, setFormParams } = useStore((state) => ({
    modelVersion: state.img2videoFormParams.modelVersion,
    setFormParams: state.setFormParams,
  }));
  const store = useStoreApi();

  const savedImagesRef = useRef<FormImagePrompt[]>([]);
  const savedTransitionsRef = useRef<MultiFrameTransition[]>([]);
  const savedFirstTransitionRef = useRef<MultiFrameTransition | undefined>(undefined);
  const isAutoSwitchingRef = useRef<boolean>(false);
  const prevModelVersionRef = useRef<string | undefined>(modelVersion);

  const buildRestoredTransitions = (modelVersion: string): MultiFrameTransition[] => {
    const formParams = store.getState().img2videoFormParams;
    const firstTransitionPrompt = formParams.textPrompt || '';
    const settingsKey = MODEL_VERSION_SETTINGS_MAP[modelVersion as keyof typeof MODEL_VERSION_SETTINGS_MAP];
    const currentSettings = formParams[settingsKey] as { duration?: number } | undefined;
    const firstTransitionDuration = currentSettings?.duration || 5;

    const savedFirstTransition = savedFirstTransitionRef.current;
    return [
      {
        duration: savedFirstTransition
          ? validateAndFixDuration(savedFirstTransition.duration, modelVersion || MODEL_VERSION.Q2)
          : firstTransitionDuration,
        prompt: savedFirstTransition?.prompt ?? firstTransitionPrompt,
        from: 0,
        to: 1,
      },
      ...savedTransitionsRef.current,
    ];
  };

  const saveExtraFramesAndTransitions = ({
    currentImages,
    currentTransitions,
    maxImageCount,
  }: {
    currentImages: FormImagePrompt[];
    currentTransitions?: MultiFrameTransition[];
    maxImageCount: number;
  }) => {
    const extraImages = currentImages.slice(maxImageCount);
    savedImagesRef.current = [...extraImages, ...savedImagesRef.current];

    setImages(currentImages.slice(0, maxImageCount));

    if (currentTransitions && currentTransitions.length > 1) {
      savedFirstTransitionRef.current = currentTransitions[0];
      const extraTransitions = currentTransitions.slice(1).map((transition) => ({
        duration: transition.duration,
        prompt: transition.prompt,
        from: transition.from,
        to: transition.to,
      }));
      savedTransitionsRef.current = [...extraTransitions, ...savedTransitionsRef.current];
      setFormParams('img2video', {
        multiFrameTransitions: undefined,
        textPrompt: currentTransitions?.[0]?.prompt || '',
      });
    }

    setCurrentTransitionIndex?.(0);
  };

  const restoreExtraFramesAndTransitions = ({
    currentImages,
    maxImageCount,
  }: {
    currentImages: FormImagePrompt[];
    maxImageCount: number;
  }) => {
    const availableSlots = maxImageCount - currentImages.length;
    const imagesToRestore = savedImagesRef.current.slice(0, availableSlots);
    const remainingImages = savedImagesRef.current.slice(availableSlots);

    const restoredImages = [...currentImages, ...imagesToRestore];
    const restoredImageCount = restoredImages.length;
    setImages(restoredImages);

    if (restoredImageCount > 2) {
      const allTransitions = buildRestoredTransitions(modelVersion || MODEL_VERSION.Q2);
      setFormParams('img2video', { multiFrameTransitions: allTransitions });
    }

    savedImagesRef.current = remainingImages;
    if (remainingImages.length === 0) {
      savedTransitionsRef.current = [];
      savedFirstTransitionRef.current = undefined;
    }
  };

  const validateAndFixFirstTransitionDuration = (currentTransitions?: MultiFrameTransition[]): boolean => {
    if (!currentTransitions || currentTransitions.length === 0 || !currentTransitions[0]) {
      return false;
    }

    const firstTransitionDuration = currentTransitions[0].duration;
    const validatedDuration = validateAndFixDuration(firstTransitionDuration, modelVersion || MODEL_VERSION.Q2);

    if (validatedDuration !== firstTransitionDuration) {
      const updatedTransitions = [...currentTransitions];
      updatedTransitions[0] = {
        ...updatedTransitions[0],
        duration: validatedDuration,
      };
      setFormParams('img2video', { multiFrameTransitions: updatedTransitions });
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (isAutoSwitchingRef.current) {
      isAutoSwitchingRef.current = false;
      savedImagesRef.current = [];
      savedTransitionsRef.current = [];
      savedFirstTransitionRef.current = undefined;
      prevModelVersionRef.current = modelVersion;
      return;
    }

    const currentImages = imagePrompts.filter((p): p is FormImagePrompt => p !== null);
    const currentImageCount = currentImages.length;
    const currentTransitions = store.getState().img2videoFormParams.multiFrameTransitions;
    const maxImageCount = getImg2VideoMaxImageCount(modelVersion);

    if (currentImageCount > maxImageCount) {
      saveExtraFramesAndTransitions({
        currentImages,
        currentTransitions,
        maxImageCount,
      });
    } else if (savedImagesRef.current.length > 0 && currentImageCount <= maxImageCount && !currentTransitions?.length) {
      restoreExtraFramesAndTransitions({
        currentImages,
        maxImageCount,
      });
    }

    validateAndFixFirstTransitionDuration(currentTransitions);

    prevModelVersionRef.current = modelVersion;
  }, [modelVersion]);

  return {
    markAsAutoSwitching: () => {
      isAutoSwitchingRef.current = true;
    },
    hasSavedImages: () => savedImagesRef.current.length > 0,
    takeImagesForModeSwitch: (): Img2VideoModeSwitchSnapshot => {
      const visibleImages = store
        .getState()
        .img2videoFormParams.imagePrompts.filter((prompt): prompt is FormImagePrompt => prompt !== null);
      const images = mergeImg2VideoModeSwitchImages(visibleImages, savedImagesRef.current);
      const currentTransitions = store.getState().img2videoFormParams.multiFrameTransitions;
      const multiFrameTransitions =
        currentTransitions && currentTransitions.length > 0
          ? currentTransitions
          : savedTransitionsRef.current.length > 0
            ? buildRestoredTransitions(modelVersion || MODEL_VERSION.Q2)
            : undefined;

      savedImagesRef.current = [];
      savedTransitionsRef.current = [];
      savedFirstTransitionRef.current = undefined;
      return { images, multiFrameTransitions };
    },
  };
};
